import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector, shallowEqual } from "react-redux";
import PropTypes from "prop-types";

// Components
import SearchDropdown from "new_components/organisms/SearchDropdown";
import Table from "new_components/organisms/Table";
import ImageButton from "new_components/molecules/ImageButton";

// Constants
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import colors from "styles/colors";
import Text from "new_components/atoms/Text";
import typography from "styles/typography";

// Providers
import { useUserSearchContext, UserSearchProvider } from "Providers/UserSearchProvider";

// Styles
const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  filter: ${({ isLoading }) => (isLoading ? "blur(4px)" : "none")};
  pointer-events: ${({ isLoading }) => (isLoading ? "none" : "auto")};
  transition: filter 0.3s ease, pointer-events 0.3s ease;
`;

const TableWrapper = styled.div`
  margin-top: 16px;
`;

const AssignSection = ({ formData, onUpdate }) => {
  const { get_employee_org_category, get_employees, coreData } = useSelector(
    (state) => ({
      get_employee_org_category: state.employee_category.employee_org_category,
      get_employees: state.employees,
      coreData: state.audit.core_data,
    }),
    shallowEqual
  );

  const { userList, groupList } = useUserSearchContext();

  const [selectedUsers, setSelectedUsers] = useState(
    Array.isArray(formData.assignees) ? formData.assignees : []
  );

  const [loadingData, setLoadingData] = useState(true);

  // Function to add an individual user if not already selected
  const handleAddUser = (user) => {
    if (user.value !== -1 && !selectedUsers.some((u) => u.value === user.value)) {
      const updatedUsers = [...selectedUsers, user];
      setSelectedUsers(updatedUsers);
      onUpdate(updatedUsers);
    }
  };

  const handleAddUserGroup = (groupUsers) => {

    // Check if the groupUsers are already in the selectedUsers array
    // ensure that they are only added once but dont remove any existing users
    const updatedUsers = [...selectedUsers, ...groupUsers];
    const uniqueUsers = updatedUsers.filter((user, index, self) =>
      index === self.findIndex((t) => t.value === user.value)
    );
    
    setSelectedUsers(uniqueUsers);
    onUpdate(uniqueUsers);
  };

  // New function: if a group is selected, add all users that belong to that group.
  const handleSelectItem = (item) => {
    if (item.type === "group") {
      // Assuming the group item has a "name_id" that matches one of the user's category "name_id"
      const groupUsers = userList.filter((user) => {
        if (user?.userCategory && user?.userCategory?.length > 0) {
          return user?.userCategory?.some((cat) => cat.value_id === item.value)
        }
        return false;
      });

      // onUpdate should be called with the groupUsers array, not the item.
      // So we will create a handleAddUserGroup function that will call onUpdate with the groupUsers array.
      if (groupUsers.length > 0) {
        handleAddUserGroup(groupUsers);
      }
    } else {
      handleAddUser(item);
    }
  };

  useEffect(() => {
    if (get_employees?.employees?.length > 0) {
      setLoadingData(false);
    }
  }, [get_employee_org_category, get_employees]);

  useEffect(() => {
    setSelectedUsers(formData.draft || []);
  }, [formData.draft]);

  if (loadingData) {
    return (
      <SectionWrapper isLoading={loadingData}>
        <p>Loading data, please wait...</p>
      </SectionWrapper>
    );
  }

  // Handle removing a user
  const handleRemoveUser = (userId) => {
    const updatedUsers = selectedUsers.filter((user) => user.value !== userId);
    setSelectedUsers(updatedUsers);
    onUpdate(updatedUsers); // Pass the array directly
  };;

  return (
    <SectionWrapper isLoading={loadingData}>
      {/* Search Dropdown */}
      <SearchDropdown
        data={userList}
        groupData={groupList}
        onSearch={(item, searchTerm) =>
          item.text.toLowerCase().includes(searchTerm.toLowerCase())
        }
        placeholder="Search User or Group"
        onSelect={handleSelectItem}
      />

      {/* Table of Assigned Users */}
      <TableWrapper>
        <Table
          columns={[
            { key: "user", label: "User" },
            { key: "action", label: "Action" },
          ]}
          data={selectedUsers.map((user) => ({
            user: user.text,
            action: (
              <ImageButton
                text="Remove"
                icon={ICON_SET.delete}
                textStyle
                color={colors.auxiliary.red[500]}
                onClick={() => handleRemoveUser(user.value)}
              />
            ),
          }))}
        />

        {/* Display a message if no users are assigned */}
        {selectedUsers.length === 0 && (
          <Text
            color={colors.text.gray}
            typographyStyle={typography.body.semibold}
          >
            No users assigned
          </Text>
        )}
      </TableWrapper>
    </SectionWrapper>
  );
};

const AssignSectionWrapper = ({ formData, onUpdate }) => {
  return (
    <UserSearchProvider>
      <AssignSection formData={formData} onUpdate={onUpdate} />
    </UserSearchProvider>
  );
};

AssignSection.propTypes = {
  formData: PropTypes.shape({
    assignees: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        text: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    draft: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        text: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
  }),
  onUpdate: PropTypes.func.isRequired, // Callback to update form data
};

export default AssignSectionWrapper;
